import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClassroomType } from '@peekapak/types/DataProtocolTypes';
import {
  addClassroomTeacher,
  CoTeacherMember,
  getCandidateCoTeachers,
  getClassroomTeachers,
  removeClassroomTeacher,
} from '../BackendInterface';
import { filterTeachers } from '../Administrator/search';
import { getLocalForage, removeLocalForage, setLocalForage } from '../aaa';
import Spinner from 'react-spinkit';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../components/SecondaryButton/SecondaryButton';
import { Dialog, DialogContent } from '../components/Dialog';
import { useSelector } from 'react-redux';
import { RootState } from '../ApplicationState';
import TypedConfirmationInput from '../components/TypedConfirmationInput';
import CoTeacherRoleBadge from '../components/CoTeacherRoleBadge';

type LoadState = 'loading' | 'loaded' | 'error';

interface Props {
  classroom: ClassroomType;
  currentUserId: string;
  mode: 'invite' | 'manage';
  onClose: () => void;
}

function getErrorMessage(action: string, error: unknown, fallback: string): string {
  const detail = error instanceof Error && error.message ? error.message : fallback;
  return `We're sorry, we couldn't complete ${action}. The server responded: ${detail}`;
}

const ManageCoTeachersModal = ({ classroom, currentUserId, mode, onClose }: Props): JSX.Element => {
  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const district = userProfile?.district ?? null;
  const [coTeachersState, setCoTeachersState] = useState<LoadState>('loading');
  const [candidateState, setCandidateState] = useState<LoadState>('loading');
  const [coTeachers, setCoTeachers] = useState<CoTeacherMember[]>([]);
  const [candidates, setCandidates] = useState<CoTeacherMember[]>([]);
  const [teacherQuery, setTeacherQuery] = useState('');
  const [teacherIndex, setTeacherIndex] = useState<number | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<CoTeacherMember | null>(null);
  const [isTeacherDropdownOpen, setIsTeacherDropdownOpen] = useState(false);
  const [isInvitingTeacher, setIsInvitingTeacher] = useState(false);
  const [inviteErrorMessage, setInviteErrorMessage] = useState<string | null>(null);
  const [pendingRemoveTeacherId, setPendingRemoveTeacherId] = useState<string | null>(null);
  const [invitedTeacherName, setInvitedTeacherName] = useState<string | null>(null);
  const [removeTargetTeacher, setRemoveTargetTeacher] = useState<CoTeacherMember | null>(null);
  const [removeConfirmationName, setRemoveConfirmationName] = useState('');
  const [removeErrorMessage, setRemoveErrorMessage] = useState<string | null>(null);
  const teacherResultId = 'teacher-search-results';

  const districtScopeKey = district ?? 'none';
  const coTeacherCacheKey = `coTeachers-${classroom.classroomId}`;
  const candidateCacheKey = `candidateCoTeachers-${classroom.classroomId}-${districtScopeKey}`;

  const invalidateTeacherCaches = useCallback(async () => {
    await Promise.all([removeLocalForage(coTeacherCacheKey), removeLocalForage(candidateCacheKey)]);
  }, [candidateCacheKey, coTeacherCacheKey]);

  const loadTeacherData = useCallback(
    async (useCache: boolean) => {
      setCoTeachersState('loading');
      setCandidateState('loading');

      const requestPayload = {
        classroomId: classroom.classroomId,
        ...(district ? { districts: [district] } : {}),
      };

      try {
        let cachedCandidates: CoTeacherMember[] | null = null;
        if (useCache) {
          cachedCandidates = (await getLocalForage(candidateCacheKey)) as CoTeacherMember[] | null;
        }

        const teachersPromise = getClassroomTeachers(classroom.classroomId);
        const candidateTeachersPromise = cachedCandidates
          ? Promise.resolve(cachedCandidates)
          : getCandidateCoTeachers(requestPayload);

        const [teachers, candidateTeachers] = await Promise.all([teachersPromise, candidateTeachersPromise]);
        setCoTeachers(teachers);
        setCandidates(candidateTeachers);
        setCoTeachersState('loaded');
        setCandidateState('loaded');

        if (!cachedCandidates) {
          await setLocalForage(candidateCacheKey, candidateTeachers, 60);
        }
      } catch (_error) {
        setCoTeachersState('error');
        setCandidateState('error');
      }
    },
    [candidateCacheKey, classroom.classroomId, district],
  );

  useEffect(() => {
    void loadTeacherData(true);
  }, [loadTeacherData]);

  const visibleCoTeachers = useMemo(() => {
    return coTeachers.filter((teacher) => teacher.userId !== currentUserId);
  }, [coTeachers, currentUserId]);

  const filteredTeachers = useMemo(() => {
    return filterTeachers(teacherQuery, candidates);
  }, [teacherQuery, candidates]);

  const occupiedTeacherCount = useMemo(() => {
    return coTeachers.filter(
      (teacher) => teacher.status === undefined || teacher.status === 'active' || teacher.status === 'pending',
    ).length;
  }, [coTeachers]);

  const isInviteLimitReached = occupiedTeacherCount >= 5;

  const modalTitle = mode === 'invite' ? 'Invite co-teacher' : 'Manage co-teachers';
  const selectedTeacherDisplay = selectedTeacher ? selectedTeacher.teacherName : 'Select a teacher';

  const handleSelectTeacher = (teacher: CoTeacherMember) => {
    setSelectedTeacher(teacher);
    setTeacherQuery(teacher.teacherName);
    setTeacherIndex(null);
    setIsTeacherDropdownOpen(false);
    setInviteErrorMessage(null);
  };

  const handleInviteTeacher = async () => {
    if (!selectedTeacher || isInvitingTeacher) {
      return;
    }

    const invitedTeacher = selectedTeacher;
    setIsInvitingTeacher(true);
    setInviteErrorMessage(null);
    try {
      await addClassroomTeacher(classroom.classroomId, invitedTeacher.userId);
      await invalidateTeacherCaches();
      setTeacherQuery('');
      setTeacherIndex(null);
      setSelectedTeacher(null);
      setIsTeacherDropdownOpen(false);
      await loadTeacherData(false);
      setInvitedTeacherName(invitedTeacher.teacherName);
    } catch (error) {
      setInviteErrorMessage(
        getErrorMessage('the invitation', error, 'Unable to invite co-teacher right now. Please try again.'),
      );
    } finally {
      setIsInvitingTeacher(false);
    }
  };

  const handleRemoveTeacher = async (teacherUserId: string): Promise<boolean> => {
    if (pendingRemoveTeacherId) {
      return false;
    }

    setPendingRemoveTeacherId(teacherUserId);
    setRemoveErrorMessage(null);
    try {
      await removeClassroomTeacher(classroom.classroomId, teacherUserId);
      await invalidateTeacherCaches();
      await loadTeacherData(false);
      return true;
    } catch (error) {
      setRemoveErrorMessage(
        getErrorMessage('the removal', error, 'Unable to remove co-teacher right now. Please try again.'),
      );
      return false;
    } finally {
      setPendingRemoveTeacherId(null);
    }
  };

  const confirmRemoveTeacher = async () => {
    if (!removeTargetTeacher) {
      return;
    }

    const removed = await handleRemoveTeacher(removeTargetTeacher.userId);
    if (removed) {
      setRemoveTargetTeacher(null);
      setRemoveConfirmationName('');
      setRemoveErrorMessage(null);
    }
  };

  const handleCloseRemoveDialog = () => {
    setRemoveTargetTeacher(null);
    setRemoveConfirmationName('');
    setRemoveErrorMessage(null);
  };

  const isRemoveNameMatch =
    removeTargetTeacher !== null &&
    removeConfirmationName.trim().toLowerCase() === removeTargetTeacher.teacherName.trim().toLowerCase();

  const isPrimaryDialogOpen = invitedTeacherName === null && removeTargetTeacher === null;

  return (
    <>
      <Dialog open={isPrimaryDialogOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className='w-[min(95vw,791px)] border-0 bg-transparent p-0 shadow-none'>
          <div className='overflow-hidden rounded-[15px] bg-white'>
            <div className='relative flex min-h-[59px] items-center justify-center bg-[#012A73] px-[1.5rem] py-[1.2rem] text-white'>
              <h2 className="m-0 max-w-[80%] text-center font-['GT-Walsheim-Pro-Regular'] text-[2.4rem] font-normal leading-[1.1]">
                {modalTitle}
              </h2>
              <button
                type='button'
                onClick={onClose}
                aria-label='Close co-teacher dialog'
                className='absolute right-[18px] top-[14px] cursor-pointer border-0 bg-transparent text-[2rem] leading-[2rem] text-white'
              >
                x
              </button>
            </div>

            <div className='flex flex-col items-center px-[2.4rem] pb-[2.4rem] pt-[2.2rem]'>
              <div className="mb-[1.2rem] text-center font-['GT-Walsheim-Pro-Regular'] text-[1.8rem] leading-[1.3] text-[#86939E]">
                You are inviting a co-teacher to:
              </div>
              <div className="mb-[2.4rem] text-center font-['GT-Walsheim-Pro-Bold'] text-[2.2rem] leading-[1.2] text-[#86939E]">
                {classroom.className || 'Main Class'}
              </div>

              <div className='mb-[1.4rem] flex w-full max-w-[574px] items-start gap-[0.8rem] rounded-[8px] border border-[#e5e9ef] bg-[#f8fafc] px-[1rem] py-[0.9rem]'>
                <span className='inline-flex h-[2rem] w-[2rem] items-center justify-center rounded-full border border-[#86939E] text-[1.2rem] text-[#86939E]'>
                  i
                </span>
                <p className="m-0 font-['GT-Walsheim-Pro-Regular'] text-[1.4rem] leading-[1.4] text-[#86939E]">
                  Co-teachers must have an existing Peekapak account at your school. Contact your administrator if a
                  teacher is missing from this list.
                </p>
              </div>

              <section className='w-full max-w-[574px] rounded-[8px] bg-[#f8fafc] px-[1.2rem] py-[1.2rem]'>
                <h3 className="m-0 mb-[2.5rem] text-center font-['GT-Walsheim-Pro-Regular'] text-[1.8rem] leading-[1.3] text-[#86939E]">
                  The following teachers are available to add to your class:
                </h3>

                <label htmlFor='teacherSearch' className='sr-only'>
                  Find teacher
                </label>
                <div className='relative mt-[1.2rem]'>
                  {candidateState === 'loading' ? (
                    <div className='flex h-[55px] w-full items-center justify-center gap-[1rem] bg-[#f8fafc]'>
                      <Spinner name='line-scale' />
                      <span className="font-['GT-Walsheim-Pro-Regular'] text-[1.4rem] text-[#86939E]">
                        Loading teachers...
                      </span>
                    </div>
                  ) : (
                    <>
                      <input
                        id='teacherSearch'
                        value={teacherQuery}
                        role='combobox'
                        aria-expanded={isTeacherDropdownOpen}
                        aria-controls={teacherResultId}
                        aria-activedescendant={
                          teacherIndex !== null && filteredTeachers[teacherIndex]
                            ? `teacher-option-${teacherIndex}`
                            : undefined
                        }
                        aria-autocomplete='list'
                        className="h-[55px] w-full rounded-[5px] border border-[#86939E] bg-white px-[1.6rem] pr-[3.5rem] font-['GT-Walsheim-Pro-Bold'] text-[1.6rem] leading-none text-[#86939E] outline-none"
                        onChange={(event) => {
                          if (!isTeacherDropdownOpen) {
                            setIsTeacherDropdownOpen(true);
                          }
                          setTeacherQuery(event.target.value);
                          setInviteErrorMessage(null);
                          setTeacherIndex(null);
                          setSelectedTeacher(null);
                        }}
                        onFocus={() => {
                          if (!selectedTeacher) {
                            setIsTeacherDropdownOpen(true);
                            setTeacherQuery('');
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'ArrowDown') {
                            if (!isTeacherDropdownOpen && !selectedTeacher) {
                              setIsTeacherDropdownOpen(true);
                            }
                            event.preventDefault();
                            setTeacherIndex((previous) => {
                              if (filteredTeachers.length === 0) {
                                return null;
                              }

                              if (previous === null) {
                                return 0;
                              }

                              return Math.min(previous + 1, Math.max(filteredTeachers.length - 1, 0));
                            });
                          }

                          if (event.key === 'ArrowUp') {
                            event.preventDefault();
                            setTeacherIndex((previous) => {
                              if (filteredTeachers.length === 0) {
                                return null;
                              }

                              if (previous === null) {
                                return 0;
                              }

                              return Math.max(previous - 1, 0);
                            });
                          }

                          if (event.key === 'Enter' && teacherIndex !== null && filteredTeachers[teacherIndex]) {
                            event.preventDefault();
                            handleSelectTeacher(filteredTeachers[teacherIndex]);
                          }

                          if (event.key === 'Escape') {
                            setTeacherIndex(null);
                            setIsTeacherDropdownOpen(false);
                          }
                        }}
                        placeholder={selectedTeacherDisplay}
                      />
                      <button
                        type='button'
                        onClick={() => {
                          if (selectedTeacher) {
                            return;
                          }

                          setIsTeacherDropdownOpen((isOpen) => !isOpen);
                        }}
                        aria-label='Toggle teacher dropdown'
                        className='absolute right-[0.6rem] top-1/2 -translate-y-1/2 border-0 bg-transparent px-[0.8rem] py-[0.4rem] text-[1rem] text-[#86939E]'
                      >
                        {isTeacherDropdownOpen ? '▲' : '▼'}
                      </button>
                    </>
                  )}
                  {isTeacherDropdownOpen && candidateState !== 'loading' && (
                    <div
                      id={teacherResultId}
                      role='listbox'
                      aria-label='Matching teachers'
                      className='absolute left-0 right-0 top-[calc(100%+8px)] z-50 rounded-[5px] border border-[#86939E] bg-white shadow-[0_8px_18px_rgba(18,39,73,0.22)]'
                    >
                      {candidateState === 'error' ? (
                        <p className='m-0 p-[0.8rem] text-[1.4rem] text-red-600'>Unable to load teachers right now.</p>
                      ) : null}

                      {candidateState === 'loaded' && filteredTeachers.length > 0 && !selectedTeacher ? (
                        <div className='max-h-[220px] overflow-y-auto'>
                          {filteredTeachers.map((teacher, index) => (
                            <button
                              id={`teacher-option-${index}`}
                              key={teacher.userId}
                              type='button'
                              className='flex w-full flex-col items-start border-0 bg-white px-[1rem] py-[0.8rem] text-left hover:bg-slate-100'
                              role='option'
                              aria-selected={teacherIndex !== null && index === teacherIndex}
                              onClick={() => handleSelectTeacher(teacher)}
                            >
                              <span className="font-['GT-Walsheim-Pro-Regular'] text-[1.4rem] text-[#86939E]">
                                {teacher.teacherName} - {teacher.email}
                                <CoTeacherRoleBadge role={teacher.role} />
                              </span>
                              <small className='text-[1.2rem] text-slate-500'>{teacher.schoolName}</small>
                            </button>
                          ))}
                        </div>
                      ) : null}

                      {candidateState === 'loaded' && filteredTeachers.length === 0 && !selectedTeacher ? (
                        <p className="m-0 py-[0.8rem] text-center font-['GT-Walsheim-Pro-Regular'] text-[1.4rem] text-[#86939E]">
                          No matching teachers found.
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className='mt-[1.8rem]'>
                  <PrimaryButton
                    small
                    orange={isInviteLimitReached}
                    className='w-full !min-h-[55px] [&>button]:!w-full [&>button]:!min-w-0'
                    disabled={isInviteLimitReached || !selectedTeacher || isInvitingTeacher}
                    onClick={() => void handleInviteTeacher()}
                  >
                    {isInviteLimitReached ? 'Limit Reached' : isInvitingTeacher ? 'Inviting...' : 'Invite co-teacher'}
                  </PrimaryButton>
                </div>

                {inviteErrorMessage ? (
                  <p className='mb-0 mt-[1.2rem] text-center text-[1.4rem] text-red-600'>{inviteErrorMessage}</p>
                ) : null}
              </section>

              <section className='mt-[1.6rem] w-full max-w-[574px] rounded-[8px] border border-[#e5e9ef] px-[1.2rem] py-[1.2rem]'>
                <h3 className="m-0 mb-[1rem] text-center font-['GT-Walsheim-Pro-Regular'] text-[1.8rem] leading-[1.3] text-[#86939E]">
                  Current co-teachers
                </h3>

                {coTeachersState === 'loading' ? (
                  <div className='flex items-center justify-center gap-[1rem] py-[1rem]'>
                    <Spinner name='line-scale' />
                    <p className="m-0 font-['GT-Walsheim-Pro-Regular'] text-[1.4rem] text-[#86939E]">
                      Loading co-teachers...
                    </p>
                  </div>
                ) : null}

                {coTeachersState === 'error' ? (
                  <p className='m-0 text-center text-[1.4rem] text-red-600'>Unable to load co-teachers right now.</p>
                ) : null}

                {coTeachersState === 'loaded' && visibleCoTeachers.length === 0 ? (
                  <p className="m-0 py-[1.6rem] text-center font-['GT-Walsheim-Pro-Regular'] text-[1.5rem] text-[#86939E]">
                    No other co-teachers yet
                  </p>
                ) : null}

                {coTeachersState === 'loaded' && visibleCoTeachers.length > 0 ? (
                  <div className='mb-[0.4rem] flex flex-col gap-[0.8rem]'>
                    {visibleCoTeachers.map((teacher) => (
                      <div
                        key={teacher.userId}
                        className='flex min-h-[55px] items-center justify-between gap-[1rem] rounded-[5px] border border-[#86939E] bg-white px-[1.2rem]'
                      >
                        <span
                          title={teacher.email || 'Email unavailable'}
                          className="font-['GT-Walsheim-Pro-Regular'] text-[1.6rem] text-[#86939E]"
                        >
                          {teacher.teacherName}
                          <CoTeacherRoleBadge role={teacher.role} />
                          {teacher.status === 'pending' ? (
                            <span className='ml-[0.6rem] rounded bg-amber-100 px-[0.5rem] py-[0.2rem] text-[1.2rem] text-amber-800'>
                              Pending
                            </span>
                          ) : null}
                        </span>
                        <SecondaryButton
                          tiny
                          className='[&>button]:!flex [&>button]:!h-[3.2rem] [&>button]:!items-center [&>button]:!justify-center [&>button]:!leading-[1]'
                          disabled={pendingRemoveTeacherId !== null}
                          onClick={() => {
                            setRemoveErrorMessage(null);
                            setRemoveTargetTeacher(teacher);
                          }}
                        >
                          {pendingRemoveTeacherId === teacher.userId ? 'Removing...' : 'Remove'}
                        </SecondaryButton>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>

              <div className='mt-[1.6rem] flex justify-center'>
                <SecondaryButton small onClick={onClose}>
                  Done
                </SecondaryButton>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={invitedTeacherName !== null} onOpenChange={(isOpen) => !isOpen && setInvitedTeacherName(null)}>
        <DialogContent className='w-[min(95vw,791px)] border-0 bg-transparent p-0 shadow-none'>
          <div className='overflow-hidden rounded-[15px] bg-white'>
            <div className='relative flex min-h-[59px] items-center justify-center bg-[#012A73] px-[1.5rem] py-[1.2rem] text-white'>
              <h2 className="m-0 max-w-[80%] text-center font-['GT-Walsheim-Pro-Regular'] text-[2.4rem] font-normal leading-[1.1]">
                Invite a co-teacher
              </h2>
              <button
                type='button'
                onClick={() => setInvitedTeacherName(null)}
                aria-label='Close invite confirmation dialog'
                className='absolute right-[18px] top-[14px] cursor-pointer border-0 bg-transparent text-[2rem] leading-[2rem] text-white'
              >
                x
              </button>
            </div>
            <div className='px-[10.9rem] pb-[7.3rem] pt-[5.6rem] text-center'>
              <p className="m-0 font-['GT-Walsheim-Pro-Bold'] text-[3rem] leading-[1.4] text-[#86939E]">
                {invitedTeacherName} has been invited as a co-teacher to {classroom.className || 'Main Class'}.
              </p>
              <p className="mb-0 mt-[2.8rem] font-['GT-Walsheim-Pro-Regular'] text-[1.8rem] leading-[1.4] text-[#86939E]">
                The invited co-teacher will receive an email and in-app notification with Accept and Decline options.
              </p>
              <div className='mt-[5.6rem]'>
                <button
                  type='button'
                  onClick={() => setInvitedTeacherName(null)}
                  className='h-[55px] w-full rounded-[5px] border-0 bg-[#00AFD8] font-["GT-Walsheim-Pro-Bold"] text-[1.8rem] font-bold text-white'
                >
                  DONE
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={removeTargetTeacher !== null} onOpenChange={(isOpen) => !isOpen && handleCloseRemoveDialog()}>
        <DialogContent className='w-[min(95vw,791px)] border-0 bg-transparent p-0 shadow-none'>
          <div className='overflow-hidden rounded-[15px] bg-white'>
            <div className='relative flex min-h-[59px] items-center justify-center bg-[#012A73] px-[1.5rem] py-[1.2rem] text-white'>
              <h2 className="m-0 max-w-[80%] text-center font-['GT-Walsheim-Pro-Regular'] text-[2.4rem] font-normal leading-[1.1]">
                Remove Co-Teacher
              </h2>
              <button
                type='button'
                onClick={handleCloseRemoveDialog}
                aria-label='Close delete co-teacher dialog'
                className='absolute right-[18px] top-[14px] cursor-pointer border-0 bg-transparent text-[2rem] leading-[2rem] text-white'
              >
                x
              </button>
            </div>
            <div className='px-[10.9rem] pb-[2.4rem] pt-[4.2rem]'>
              <p className="m-0 font-['GT-Walsheim-Pro-Regular'] text-[1.8rem] leading-[1.4] text-[#86939E]">
                Are you sure you want to delete this co-teacher? This will remove their access to '
                {classroom.className || 'Main Class'}' and related data, including all student work associated with this
                class. The teacher can be added back at a later date if needed.
              </p>
              <p className="mb-0 mt-[1.2rem] font-['GT-Walsheim-Pro-Regular'] text-[1.8rem] leading-[1.4] text-[#86939E]">
                To confirm, please type out the name of the co-teacher below.
              </p>

              {removeErrorMessage ? (
                <p className='mb-0 mt-[1.2rem] text-[1.4rem] text-red-600'>{removeErrorMessage}</p>
              ) : null}

              <div className='mt-[1rem]'>
                <label htmlFor='removeTeacherName' className='sr-only'>
                  Type full teacher name to confirm removal
                </label>
                <TypedConfirmationInput
                  id='removeTeacherName'
                  value={removeConfirmationName}
                  onChange={(event) => setRemoveConfirmationName(event.target.value)}
                  placeholder={removeTargetTeacher?.teacherName || ''}
                />
              </div>
              <div className='mt-[2.6rem] flex justify-center'>
                <PrimaryButton
                  type='button'
                  small
                  alert
                  disabled={pendingRemoveTeacherId !== null || !isRemoveNameMatch}
                  onClick={() => void confirmRemoveTeacher()}
                >
                  {pendingRemoveTeacherId !== null ? 'REMOVING...' : 'REMOVE'}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageCoTeachersModal;
